import { z } from "zod";
/**
 * Group-level role held by a participant. Roles are role-based at the group level —
 * every action on a group, expense, or comment is gated by the caller's role in
 * the parent group.
 *
 * - `OWNER`  can do everything in the group, including manage participants and the public invite.
 * - `MEMBER` can read the group and create/edit/delete their own expenses and comments.
 * - `VIEWER` is read-only.
 */
export const RoleSchema = z.enum(["OWNER", "MEMBER", "VIEWER"]);
/** Roles a join request can propose / be approved with. `OWNER` is intentionally not allowed. */
export const ClaimableRoleSchema = z.enum(["MEMBER", "VIEWER"]);
/** A participant kind. `GUEST` participants cannot themselves authenticate. */
export const ParticipantTypeSchema = z.enum(["USER", "GUEST"]);
/** Empty-success response shape for mutating endpoints. */
export const SuccessResponseSchema = z.strictObject({
    success: z.literal(true),
});
/** Uniform error body returned by every non-2xx response. */
export const ApiErrorCodeSchema = z.enum([
    "VALIDATION_ERROR",
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
    "CONFLICT",
    "GONE",
    "UNPROCESSABLE",
    "TOO_MANY_REQUESTS",
    "INTERNAL",
]);
export const ApiErrorDetailSchema = z.strictObject({
    path: z.string(),
    message: z.string(),
});
export const ApiErrorSchema = z.strictObject({
    error: z.strictObject({
        code: ApiErrorCodeSchema,
        message: z.string(),
        details: z.array(ApiErrorDetailSchema).optional(),
    }),
});
/**
 * Cursor-based pagination envelope. The cursor is opaque (base64-encoded
 * DynamoDB `LastEvaluatedKey`) and must not be parsed by clients.
 */
export const paginatedEnvelopeSchema = (item) => z.strictObject({
    items: z.array(item),
    nextCursor: z.string().optional(),
});
/** Common shape of a `?cursor=&limit=` query string for list endpoints. */
export const PaginationQuerySchema = z.strictObject({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().optional(),
});
